import { api } from './api';

/**
 * Integration status for a single tool
 */
export interface IntegrationStatus {
  tool: string;
  connected: boolean;
  expired: boolean;
  expiresAt?: string;
  lastRefreshed?: string;
  message?: string;
  connectedToolsCount?: number;
}

/**
 * Response from the unified site integrations endpoint
 */
export interface SiteIntegrationsResponse {
  siteId: number;
  gsc: IntegrationStatus;
  bing: IntegrationStatus;
  configuredCount: number;
  expiredCount: number;
  totalTools: number;
}

interface ApiResponse<T> {
  data: T;
  success: boolean;
  requestId: string;
  timestamp: number;
}

/**
 * Service for fetching unified site integration status.
 * Provides a single endpoint to get OAuth connection status for all tools.
 */
export const siteIntegrationService = {
  /**
   * Get integration status for all tools for a site in a single API call.
   * Returns OAuth connection status for GSC, GA4, Bing, and Pinterest.
   * 
   * @param siteId - The site ID to check integrations for
   * @returns Promise with integration status for all tools
   */
  getIntegrationStatus: async (siteId: number): Promise<SiteIntegrationsResponse> => {
    const response = await api.get<ApiResponse<SiteIntegrationsResponse>>(
      `/customer/sites/${siteId}/integrations`
    );
    return response.data.data;
  },

  /**
   * Check if any tools have expired tokens that need reconnection.
   * 
   * @param status - The integration status response
   * @returns true if any tool has an expired token
   */
  hasExpiredTokens: (status: SiteIntegrationsResponse): boolean => {
    return status.expiredCount > 0;
  },

  /**
   * Get list of tools with expired tokens.
   * 
   * @param status - The integration status response
   * @returns Array of tool names with expired tokens
   */
  getExpiredTools: (status: SiteIntegrationsResponse): string[] => {
    const tools: string[] = [];
    if (status.gsc.expired) tools.push('GSC');
    if (status.bing.expired) tools.push('Bing');
    return tools;
  },

  /**
   * Get list of tools that are connected (regardless of expiration).
   * 
   * @param status - The integration status response
   * @returns Array of connected tool names
   */
  getConnectedTools: (status: SiteIntegrationsResponse): string[] => {
    const tools: string[] = [];
    if (status.gsc.connected) tools.push('GSC');
    if (status.bing.connected) tools.push('Bing');
    return tools;
  },

  /**
   * Get list of tools that are not connected.
   * 
   * @param status - The integration status response
   * @returns Array of not connected tool names
   */
  getNotConnectedTools: (status: SiteIntegrationsResponse): string[] => {
    const tools: string[] = [];
    if (!status.gsc.connected) tools.push('GSC');
    if (!status.bing.connected) tools.push('Bing');
    return tools;
  }
};
